import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Check, Package, Tag } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function ProMiniSiteCatalog() {
  const [rubriques, setRubriques] = useState([]);
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pro/minisite/catalog")
      .then(({ data }) => { setRubriques(data.rubriques || []); setProducts(data.products || []); })
      .catch(() => setLoadError(true))
      .finally(() => setLoaded(true));
  }, []);

  const addRubrique = () => setRubriques((r) => [...r, { id: uid(), name: "", sort_order: r.length }]);
  const setRub = (i, v) => setRubriques((r) => r.map((x, j) => (j === i ? { ...x, name: v } : x)));
  const delRub = (i) => setRubriques((r) => r.filter((_, j) => j !== i));

  const addProduct = () => setProducts((p) => [...p, {
    id: uid(), rubrique_id: "", name: "", description: "", image: "", price: "",
    price_from: false, badges: [], variants: [], sort_order: p.length,
  }]);
  const setProd = (i, patch) => setProducts((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delProd = (i) => setProducts((p) => p.filter((_, j) => j !== i));
  const moveProd = (i, dir) => setProducts((p) => {
    const a = [...p]; const j = i + dir;
    if (j < 0 || j >= a.length) return p;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });

  const addVariant = (pi) => setProd(pi, { variants: [...(products[pi].variants || []), { name: "", price: "" }] });
  const setVariant = (pi, vi, patch) => setProd(pi, {
    variants: products[pi].variants.map((v, j) => (j === vi ? { ...v, ...patch } : v)),
  });
  const delVariant = (pi, vi) => setProd(pi, { variants: products[pi].variants.filter((_, j) => j !== vi) });

  const save = async () => {
    if (loadError) { toast.error("Le catalogue n'a pas pu être chargé — rechargez la page avant d'enregistrer."); return; }
    setSaving(true);
    try {
      const payload = {
        rubriques: rubriques.filter((r) => (r.name || "").trim()).map((r) => ({ id: r.id, name: r.name.trim() })),
        products: products.filter((p) => (p.name || "").trim()).map((p) => ({
          id: p.id, rubrique_id: p.rubrique_id || "", name: p.name.trim(), description: p.description || "",
          image: p.image || "", price: p.price || "", price_from: !!p.price_from,
          badges: (Array.isArray(p.badges) ? p.badges : String(p.badges || "").split(",")).map((b) => b.trim()).filter(Boolean),
          variants: (p.variants || []).filter((v) => (v.name || "").trim()),
        })),
      };
      const { data } = await api.put("/pro/minisite/catalog", payload);
      setRubriques(data.rubriques || []); setProducts(data.products || []);
      toast.success("Catalogue enregistré ✅");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement."); }
    setSaving(false);
  };

  if (!loaded) return <p className="text-slate-400">Chargement…</p>;
  if (loadError) return <p className="text-rose-500" data-testid="catalog-load-error">Impossible de charger le catalogue. Rechargez la page.</p>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="pro-catalog">
      <p className="text-sm text-slate-500">
        Composez le catalogue affiché sur la page <strong>Produits & Services</strong> de votre mini-site. Regroupez vos articles par rubriques.
      </p>

      {/* Rubriques */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Tag size={18} className="text-[#C84B31]" /><h3 className="font-heading font-bold text-lg">Rubriques</h3></div>
        <div className="space-y-2">
          {rubriques.map((r, i) => (
            <div key={r.id} className="flex items-center gap-2" data-testid={`rub-row-${i}`}>
              <Input value={r.name} onChange={(e) => setRub(i, e.target.value)} placeholder="Ex. Viennoiseries" data-testid={`rub-name-${i}`} />
              <button onClick={() => delRub(i)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`rub-del-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addRubrique} data-testid="rub-add"><Plus size={15} className="mr-1.5" /> Ajouter une rubrique</Button>
        </div>
      </div>

      {/* Produits */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Package size={18} className="text-[#C84B31]" /><h3 className="font-heading font-bold text-lg">Produits & services</h3></div>
        <div className="space-y-4">
          {products.map((p, i) => (
            <div key={p.id} className="rounded-xl border border-slate-200 p-4 bg-slate-50/50" data-testid={`prod-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Article {i + 1}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveProd(i, -1)} disabled={i === 0} className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30" data-testid={`prod-up-${i}`}><ArrowUp size={14} /></button>
                  <button onClick={() => moveProd(i, 1)} disabled={i === products.length - 1} className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30" data-testid={`prod-down-${i}`}><ArrowDown size={14} /></button>
                  <button onClick={() => delProd(i)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" data-testid={`prod-del-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Nom</Label><Input value={p.name} onChange={(e) => setProd(i, { name: e.target.value })} data-testid={`prod-name-${i}`} /></div>
                <div>
                  <Label>Rubrique</Label>
                  <Select value={p.rubrique_id || "none"} onValueChange={(v) => setProd(i, { rubrique_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid={`prod-rub-${i}`}><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {rubriques.filter((r) => (r.name || "").trim()).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={p.description} onChange={(e) => setProd(i, { description: e.target.value })} data-testid={`prod-desc-${i}`} /></div>
                <div>
                  <Label>Prix</Label>
                  <Input value={p.price} onChange={(e) => setProd(i, { price: e.target.value })} placeholder="Ex. 4,50 €" data-testid={`prod-price-${i}`} />
                  <label className="flex items-center gap-2 mt-1.5 text-xs text-slate-600">
                    <input type="checkbox" checked={!!p.price_from} onChange={(e) => setProd(i, { price_from: e.target.checked })} data-testid={`prod-pricefrom-${i}`} /> Afficher « À partir de »
                  </label>
                </div>
                <div>
                  <Label>Badges (séparés par virgule)</Label>
                  <Input value={Array.isArray(p.badges) ? p.badges.join(", ") : (p.badges || "")}
                    onChange={(e) => setProd(i, { badges: e.target.value })}
                    placeholder="Bio, Spécialité locale" data-testid={`prod-badges-${i}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Photo</Label>
                  <ImageUploader value={p.image ? [p.image] : []} max={1} testId={`prod-image-${i}`}
                    onChange={(arr) => setProd(i, { image: arr[0] || "" })} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Variantes / options</Label>
                  <div className="space-y-2 mt-1">
                    {(p.variants || []).map((v, vi) => (
                      <div key={vi} className="flex items-center gap-2">
                        <Input value={v.name} onChange={(e) => setVariant(i, vi, { name: e.target.value })} placeholder="Nom (ex. Grande taille)" className="h-9" data-testid={`prod-var-name-${i}-${vi}`} />
                        <Input value={v.price} onChange={(e) => setVariant(i, vi, { price: e.target.value })} placeholder="Prix" className="h-9 w-28" data-testid={`prod-var-price-${i}-${vi}`} />
                        <button onClick={() => delVariant(i, vi)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => addVariant(i)} data-testid={`prod-var-add-${i}`}><Plus size={14} className="mr-1" /> Ajouter une variante</Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addProduct} data-testid="prod-add"><Plus size={15} className="mr-1.5" /> Ajouter un produit / service</Button>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="btn-shine text-white" data-testid="catalog-save-btn">
        <Check size={16} className="mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer le catalogue"}
      </Button>
    </div>
  );
}
